import { type IMediaInstance } from "@pixi/sound"

/**
 * The Ember Core's furnace loop, owned the same way BonecallerChant owns the ritual chant: one
 * instance, started lazily, and safe against a stop that lands while the audio is still decoding.
 *
 * What it adds is PITCH. The loop itself is deliberately steady (see ember_core_charge in gen.py);
 * the rising tension of a charge comes from ramping playback rate and gain together across the hold.
 * That is also why it is a loop and not a one-shot 800ms sample: the hold can be released early, the
 * profile's chargeMs is configurable per weapon, and a sample would have to be re-cut for each.
 *
 * Volume and speed are both eased toward their targets rather than set, so the step between server
 * messages never clicks.
 */
const EASE_MS = 80
const MIN_SPEED = 0.72

export class EmberCoreCharge {
    private track?: { key: string; player?: IMediaInstance; volume: number; speed: number }
    private lastAt = 0
    private retryAt = 0

    constructor(private play: () => IMediaInstance | Promise<IMediaInstance>) {}

    stop = () => {
        const track = this.track
        this.track = undefined
        track?.player?.stop()
    }

    update = (key: string | undefined, volume: number, speed: number, now: number) => {
        const elapsed = Math.min(100, Math.max(0, now - this.lastAt))
        this.lastAt = now
        if (!key) { this.stop(); return }
        if (this.track && this.track.key !== key) this.stop()
        if (!this.track && volume > 0 && now >= this.retryAt) {
            const track = { key, volume: 0, speed: MIN_SPEED, player: undefined as IMediaInstance | undefined }
            this.track = track
            // A stopped/replaced request must never start sounding when decoding finishes.
            Promise.resolve().then(() => this.track === track ? this.play() : undefined).then(player => {
                if (!player) return
                if (this.track !== track) { player.stop(); return }
                track.player = player
                player.set("volume", track.volume)
                player.set("speed", track.speed)
            }).catch(() => {
                if (this.track === track) { this.track = undefined; this.retryAt = now + 1000 }
            })
        }
        const track = this.track
        if (!track) return
        if (!track.player && volume === 0) { this.stop(); return }
        const ease = 1 - Math.exp(-elapsed / EASE_MS)
        track.volume += (volume - track.volume) * ease
        track.speed += (speed - track.speed) * ease
        track.player?.set("volume", track.volume)
        track.player?.set("speed", track.speed)
        if (volume === 0 && track.volume < 0.001) this.stop()
    }
}
